import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
    Box,
    IconButton,
    Typography,
    useTheme,
    Paper,
    Button,
    Stack,
    Container,
    alpha,
    TextField,
    InputAdornment,
    Tooltip,
    Snackbar,
    Alert
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { addFunds } from '../../services/backendAPI';
import AddBankModal from './AddBankModal';
import { fetchProfileDataAction } from '../../store/actions/profile';


const PayIn = forwardRef(({ onClose, isModal, setIsPaymentGuidelinesModalOpen }, ref) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();

    // Get user data from Redux store
    const userData = useSelector(state => state.profile.userData);
    const bankAccounts = userData?.bank_accounts || [];

    // Amount state and validation
    const [amount, setAmount] = useState('1000');
    const [amountError, setAmountError] = useState('');

    // Modify snackbar state to be simpler
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'error'
    });

    const [showAddBankModal, setShowAddBankModal] = useState(false);
    const [selectedBank, setSelectedBank] = useState('');

    // Set default selected bank when component mounts or bank accounts change
    useEffect(() => {
        if (bankAccounts.length > 0) {
            setSelectedBank(bankAccounts[0].bank_account_number);
        }
    }, [bankAccounts]);

    useImperativeHandle(ref, () => ({
        handleAddFunds,
    }));

    const handleAmountChange = (event) => {
        const value = event.target.value;
        // Remove any non-numeric characters except decimal point
        const cleanValue = value.replace(/[^\d.]/g, '');
        // Ensure only one decimal point
        const parts = cleanValue.split('.');
        const formattedValue = parts[0] + (parts.length > 1 ? '.' + parts[1].slice(0, 2) : '');

        // Validate amount
        if (parseFloat(formattedValue) < 1000) {
            setAmountError('Minimum amount is ₹1000');
            // } else if (parseFloat(formattedValue) > 1000000) {
            //     setAmountError('Maximum amount is ₹10,00,000');
        } else {
            setAmountError('');
        }

        setAmount(formattedValue);
    };

    const handleQuickAmountSelect = (selectedAmount) => {
        setAmount(selectedAmount.toString());
        setAmountError(''); // Clear any existing errors when selecting a quick amount
    };

    const quickAmounts = [1000, 2000, 5000, 10000, 25000, 100000];

    const handleClose = () => {
        if (onClose) {
            onClose();
        } else {
            dispatch({
                type: 'SET_NAVIGATED_TO_PAYIN',
                payload: false
            });
            navigate(-1);
        }
    };

    const handleAddBankClick = () => {
        setShowAddBankModal(true);
    };

    const handleAddBankClose = () => {
        setShowAddBankModal(false);
    };

    const handleAddFunds = async () => {
        let newWindow;
        try {
            // Safari popup workaround
            const isSafari = typeof window !== 'undefined' &&
                /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
            if (isSafari) {
                newWindow = window.open('', '_blank');
            }
            let payload = {
                amount: String(Number(amount) * 100),
                client_id: localStorage.getItem("login_id"),
            };

            // Only add bank details if a bank is selected
            const selectedBankAccount = bankAccounts.find(bank => bank.bank_account_number === selectedBank);
            if (selectedBankAccount) {
                payload = {
                    ...payload,
                    bank_account_number: selectedBankAccount.bank_account_number,
                    bank_name: selectedBankAccount.bank_name,
                    ifsc: selectedBankAccount.ifsc,
                };
            }

            const { data } = await addFunds(payload);

            if (data?.data?.payUrl) {
                if (newWindow) {
                    newWindow.location = data.data.payUrl;
                } else {
                    window.open(data.data.payUrl, "_blank");
                }
            } else {
                if (newWindow) newWindow.close();
                throw new Error('Payment URL not received');
            }
        } catch (error) {
            if (newWindow) newWindow.close();
            setSnackbar({
                open: true,
                message: error?.message || error || 'Failed to initiate payment',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const isValidAmount = amount && !amountError && parseFloat(amount) >= 1000;

    return (
        <Box sx={
            isModal
                ? {
                    width: '100%',
                    bgcolor: theme.palette.background.default,
                    px: 0,
                    borderRadius: 3,
                    boxShadow: 0,
                    position: 'relative',
                    maxWidth: 500,
                    margin: '0 auto',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    [theme.breakpoints.down('md')]: {
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        width: '100%',
                        height: '100%',
                        maxWidth: '100%',
                        maxHeight: '100%',
                        borderRadius: 0,
                        zIndex: 1300,
                        display: 'flex',
                        flexDirection: 'column',
                        px: 0,
                        py: 0,
                    }
                }
                : {
                    height: '100vh',
                    width: '100vw',
                    bgcolor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.95)
                        : alpha(theme.palette.grey[100], 0.95),
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    zIndex: 1300,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    [theme.breakpoints.down('md')]: {
                        position: 'absolute',
                        minHeight: '100vh',
                        minHeight: '100dvh',
                        height: 'auto',
                    }
                }
        }>
            {(!isModal) && (
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        zIndex: 1,
                        color: 'text.primary',
                        bgcolor: 'background.paper',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        '&:hover': {
                            bgcolor: 'background.paper',
                        },
                        [theme.breakpoints.down('md')]: {
                            position: isModal ? 'absolute' : 'fixed',
                            right: 8,
                            top: 8,
                            width: 40,
                            height: 40,
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            )}
            <Box sx={{
                [theme.breakpoints.down('md')]: {
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }
            }}>
                {isModal ? (
                    <Box sx={{ height: 'auto', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: theme.palette.background.default, scrollBehavior: 'smooth', position: 'relative' }}>
                        {/* Sticky Header */}
                        <Box
                            sx={{
                                display: { xs: 'flex', sm: 'flex', md: 'none' },
                                background: theme => theme.palette.background.header,
                                color: theme => theme.palette.text.header,
                                px: { xs: 0, sm: 0, md: 1.5 },
                                py: { xs: 1.5, sm: 2.5 },
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                borderBottom: '1px solid rgba(255,255,255,0.08)', // Divider is fine as alpha, or could use theme divider
                                position: 'sticky',
                                top: 0,
                                zIndex: 20,
                                width: '100%',
                                minHeight: { xs: 60, sm: 'unset' },
                                
                            }}
                        >
                            <Box sx={{ ml: { xs: '19px', sm: '19px', md: 0 } }}>
                                <Typography variant="h6" sx={{ color: theme => theme.palette.text.header, fontWeight: 700, mb: 0.5, fontSize: '1.25rem' }}>Add Funds</Typography>
                                <Typography variant="caption" sx={{ color: theme => theme.palette.text.header, opacity: 0.8 }}>Add money to your trading account</Typography>
                            </Box>
                            <IconButton
                                onClick={handleClose}
                                sx={{ color: theme => theme.palette.text.header, ml: 2, background: 'rgba(255,255,255,0.08)', '&:hover': { background: 'rgba(255,255,255,0.18)' } }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        {/* Scrollable Content - REMOVE Container for mobile modal */}
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', scrollBehavior: 'smooth' }}>
                            {/* Place all modal content (Paper, etc.) directly here, do not wrap in Container for mobile modal */}
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    border: `1px solid ${theme.palette.divider}`,
                                    [theme.breakpoints.down('md')]: {
                                        borderRadius: 3,
                                    }
                                }}
                            >
                                <Box sx={{
                                    p: 3,
                                    bgcolor: theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.success.main, 0.1)
                                        : alpha(theme.palette.success.light, 0.1),
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    [theme.breakpoints.down('md')]: {
                                        p: 2,
                                    }
                                }}>
                                    <Typography variant="h6" sx={{
                                        color: theme.palette.success.main,
                                        fontWeight: 600,
                                        [theme.breakpoints.down('md')]: {
                                            fontSize: '1.1rem',
                                        }
                                    }}>
                                        Enter Amount
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                        Enter the amount you want to add to your trading account
                                    </Typography>
                                </Box>

                                <Box sx={{
                                    px: 3,
                                    pt: 3,
                                    pb: 1,
                                    [theme.breakpoints.down('md')]: {
                                        px: 2,
                                        pt: 2,
                                        pb: 0.5,
                                    }
                                }}>
                                    <TextField
                                        fullWidth
                                        label="Amount"
                                        value={amount}
                                        onChange={handleAmountChange}
                                        error={Boolean(amountError)}
                                        helperText={amountError}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">₹</InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Min: ₹1000 | Max: ₹10,00,000">
                                                        <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                    </Tooltip>
                                                </InputAdornment>
                                            )
                                        }}
                                        sx={{
                                            '& input': {
                                                fontSize: '1.5rem',
                                                fontWeight: 500,
                                                textAlign: 'center',
                                                py: 2,
                                                [theme.breakpoints.down('md')]: {
                                                    fontSize: '1.25rem',
                                                    py: 1.5,
                                                }
                                            }
                                        }}
                                    />

                                    {/* Quick Amount Buttons */}
                                    <Box sx={{
                                        mt: 3,
                                        mb: 0,
                                        [theme.breakpoints.down('md')]: {
                                            mt: 2,
                                            mb: 0
                                        }
                                    }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                                            Quick Select
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            overflowX: 'auto',
                                            gap: 1,
                                            pb: 0,
                                            mb: 0,
                                            '&::-webkit-scrollbar': {
                                                height: 4,
                                            },
                                            '&::-webkit-scrollbar-track': {
                                                backgroundColor: 'transparent',
                                            },
                                            '&::-webkit-scrollbar-thumb': {
                                                backgroundColor: theme.palette.divider,
                                                borderRadius: 2,
                                            },
                                            '&::-webkit-scrollbar-thumb:hover': {
                                                backgroundColor: theme.palette.primary.main,
                                            },
                                            [theme.breakpoints.down('md')]: {
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(2, 1fr)',
                                                gap: 1,
                                                overflowX: 'visible',
                                                pb: 0,
                                                mb: 0,
                                            }
                                        }}>
                                            {quickAmounts.map((quickAmount) => {
                                                const formatAmount = (amount) => {
                                                    if (amount >= 1000) {
                                                        return `${amount / 1000}k`;
                                                    }
                                                    return amount.toString();
                                                };

                                                return (
                                                    <Button
                                                        key={quickAmount}
                                                        variant={amount === quickAmount.toString() ? "contained" : "outlined"}
                                                        onClick={() => handleQuickAmountSelect(quickAmount)}
                                                        size="small"
                                                        sx={{
                                                            py: 0.5,
                                                            px: 1,
                                                            fontSize: '0.75rem',
                                                            fontWeight: 500,
                                                            borderRadius: 1.5,
                                                            textTransform: 'none',
                                                            minHeight: 28,
                                                            minWidth: 60,
                                                            flexShrink: 0,
                                                            whiteSpace: 'nowrap',
                                                            '&:hover': {
                                                                transform: 'translateY(-1px)',
                                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                            },
                                                            transition: 'all 0.2s ease',
                                                            [theme.breakpoints.down('md')]: {
                                                                fontSize: '0.7rem',
                                                                py: 0.4,
                                                                px: 0.8,
                                                                minHeight: 26,
                                                                minWidth: 'auto',
                                                                flexShrink: 1,
                                                            }
                                                        }}
                                                    >
                                                        ₹{formatAmount(quickAmount)}
                                                    </Button>
                                                );
                                            })}
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>
                            {/* Sticky Add Funds Button */}
                            <Box
                                sx={{
                                    position: 'sticky',
                                    bottom: 0,
                                    width: '100%',
                                    bgcolor: 'background.paper',
                                    px: 2,
                                    pt: 2,
                                    pb: 2,
                                    borderTop: theme => `1px solid ${theme.palette.divider}`,
                                    zIndex: 10,
                                    boxShadow: '0 -2px 8px rgba(0,0,0,0.04)',
                                    [theme.breakpoints.down('md')]: {
                                        position: 'sticky',
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        px: 2,
                                        pt: 2,
                                        pb: 'calc(16px + env(safe-area-inset-bottom))',
                                        bgcolor: theme.palette.background.paper,
                                        zIndex: 11,
                                        transform: 'none'
                                    }
                                }}
                            >
                                <Button
                                    variant="contained"
                                    size="large"
                                    fullWidth
                                    endIcon={<ArrowForwardIcon />}
                                    onClick={() => setIsPaymentGuidelinesModalOpen(true)}
                                    disabled={!isValidAmount}
                                    sx={{
                                        py: 1.5,
                                        mb: 0,
                                        borderRadius: 2,
                                        bgcolor: theme => theme.palette.success.main,
                                        '&:hover': { bgcolor: theme => theme.palette.success.dark },
                                        '&:disabled': {
                                            bgcolor: theme => theme.palette.action.disabled,
                                            color: theme => theme.palette.action.disabled,
                                        },
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                    }}
                                >
                                    Add Funds
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{
                        flex: 1,
                        overflowY: 'auto',
                        minHeight: 0,
                        WebkitOverflowScrolling: 'touch',
                        pb: '80px',
                        [theme.breakpoints.down('md')]: {
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            pb: '80px',
                        }
                    }}>
                        <Container
                            maxWidth="md"
                            sx={isModal ? {
                                py: 2,
                                mt: 2,
                                pb: 2,
                                px: 0,
                                [theme.breakpoints.down('md')]: {
                                    py: 0,
                                    mt: 0,
                                    pb: 0,
                                    px: 0,
                                    height: '100%',
                                    marginLeft: 0,
                                    marginRight: 0,
                                    width: '100%',
                                    maxWidth: '100%',
                                }
                            } : {
                                py: 4,
                                mt: 4,
                                pb: 6,
                                [theme.breakpoints.down('md')]: {
                                    px: 2,
                                    py: 3,
                                    mt: 6,
                                    pb: 8,
                                }
                            }}
                        >
                            <Box sx={{
                                maxWidth: 600,
                                margin: '0 auto',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 3,
                                [theme.breakpoints.down('md')]: {
                                    gap: 2,
                                    maxWidth: '100%',
                                    margin: 0,
                                    px: 2,
                                    mb: 2
                                }
                            }}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        border: `1px solid ${theme.palette.divider}`,
                                        [theme.breakpoints.down('md')]: {
                                            borderRadius: 3,
                                        }
                                    }}
                                >
                                    <Box sx={{
                                        p: 3,
                                        bgcolor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.success.main, 0.1)
                                            : alpha(theme.palette.success.light, 0.1),
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                        [theme.breakpoints.down('md')]: {
                                            p: 2,
                                        }
                                    }}>
                                        <Typography variant="h6" sx={{
                                            color: theme.palette.success.main,
                                            fontWeight: 600,
                                            [theme.breakpoints.down('md')]: {
                                                fontSize: '1.1rem',
                                            }
                                        }}>
                                            Enter Amount
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Enter the amount you want to add to your trading account
                                        </Typography>
                                    </Box>

                                    <Box sx={{
                                        p: 3,
                                        [theme.breakpoints.down('md')]: {
                                            p: 2,
                                        }
                                    }}>
                                        <TextField
                                            fullWidth
                                            label="Amount"
                                            value={amount}
                                            onChange={handleAmountChange}
                                            error={Boolean(amountError)}
                                            helperText={amountError}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">₹</InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <Tooltip title="Min: ₹1000 | Max: ₹10,00,000">
                                                            <InfoOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                                        </Tooltip>
                                                    </InputAdornment>
                                                )
                                            }}
                                            sx={{
                                                '& input': {
                                                    fontSize: '1.5rem',
                                                    fontWeight: 500,
                                                    textAlign: 'center',
                                                    py: 2,
                                                    [theme.breakpoints.down('md')]: {
                                                        fontSize: '1.25rem',
                                                        py: 1.5,
                                                    }
                                                }
                                            }}
                                        />
                                    </Box>
                                </Paper>
                            </Box>
                        </Container>
                    </Box>
                )}
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{
                    mt: 7,
                    mr: 2,
                    [theme.breakpoints.down('md')]: {
                        mt: 8,
                        mr: 1,
                        ml: 1,
                        '& .MuiSnackbar-root': {
                            left: 8,
                            right: 8,
                        }
                    }
                }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        minWidth: '300px',
                        boxShadow: theme => `0 4px 12px ${alpha(theme.palette.error.main, 0.15)}`,
                        '& .MuiAlert-message': {
                            fontSize: '0.875rem',
                            fontWeight: 500
                        },
                        '& .MuiAlert-icon': {
                            fontSize: '1.25rem'
                        },
                        [theme.breakpoints.down('md')]: {
                            minWidth: 'auto',
                            width: '100%',
                        }
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <AddBankModal
                open={showAddBankModal}
                onClose={handleAddBankClose}
            />
        </Box>
    );
});

export default PayIn; 